import { z } from 'zod';
import { ModeSchema } from '../common';
import { CoreDataSchema } from './core';
import { ExecDataSchema } from './exec';
import { ReviewDataSchema } from './review';
import { JournalDataSchema } from './journal';
import { AiDataSchema } from './ai';
import { ScoreDataSchema } from './score';
import { DigestDataSchema } from './digest';
import { CompareDataSchema } from './compare';
import { AnalyticsDataSchema } from './analytics';
import { ProgressDataSchema } from './progress';
import { EodDataSchema } from './eod';
import { StyleDataSchema } from './style';
import { DashboardDataSchema } from './dashboard';

export * from './core';
export * from './exec';
export * from './review';
export * from './journal';
export * from './ai';
export * from './score';
export * from './digest';
export * from './compare';
export * from './analytics';
export * from './progress';
export * from './eod';
export * from './style';
export * from './dashboard';
export * from './ledger';
export * from './plans';

/** The assembled Phase-1 read model. Each slice is served by its API family; the sim client returns it in-process. */
export const DemoDataSchema = z
  .object({
    /** `mode` is the aggregate's provenance, and it is the weakest of its parts: while any contributing family is
     *  still a mock the honest answer for the whole is `simulated`, whatever the live ones report individually. */
    meta: z
      .object({
        seed: z.string(),
        mode: ModeSchema,
        asOf: z.string(),
        /** Surfaces that answered "there is nothing here" rather than answering with data.
         *
         *  The composed dataset used to be all-or-nothing: one read refusing unwound the whole tree and the product
         *  showed an error page instead of itself. A brand-new account refuses on several — no replay prepared, no
         *  report issued, no brief generated — and every one of those is an ordinary Tuesday, not a fault.
         *
         *  So absence is now data, and this is where it is recorded. A card fed by a listed surface is genuinely
         *  empty and must SAY so; without this list an empty card and a card whose data failed to arrive would look
         *  identical, which is the "never fake completeness" rule failing quietly rather than loudly. */
        degraded: z.array(z.object({ surface: z.string(), code: z.string() }).strict()).default([]),
      })
      .strict(),
    core: CoreDataSchema,
    exec: ExecDataSchema,
    review: ReviewDataSchema,
    journal: JournalDataSchema,
    ai: AiDataSchema,
    score: ScoreDataSchema,
    digest: DigestDataSchema,
    compare: CompareDataSchema,
    analytics: AnalyticsDataSchema,
    progress: ProgressDataSchema,
    eod: EodDataSchema,
    style: StyleDataSchema,
    dashboard: DashboardDataSchema,
  })
  .strict();
export type DemoData = z.infer<typeof DemoDataSchema>;
